import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Sparkles, Upload, X, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { COLLECTIONS, PERLE_OPTIONS, INVESTMENT_LEVELS } from "@/lib/collections";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const STEPS = ["Coordonnées", "Espace", "Besoins", "Confirmation"];

const initialData = {
    first_name: "", last_name: "", phone: "", email: "", city: "", address: "",
    property_type: "residentiel", collection: "", establishment_type: "",
    rooms: "", bathrooms: "", frequency: "", preferred_date: "",
    description: "", contact_method: "phone",
    perle_options: [], investment_level: "",
    consent: false, privacy_accepted: false, photo_ids: [],
    honeypot: "",
};

export default function Soumission() {
    const [params] = useSearchParams();
    const [step, setStep] = useState(0);
    const [data, setData] = useState(() => {
        const saved = localStorage.getItem("kgd_soumission_draft");
        const base = saved ? { ...initialData, ...JSON.parse(saved) } : initialData;
        const pre = params.get("collection");
        if (pre) base.collection = pre;
        const opt = params.get("option");
        if (opt && !base.perle_options.includes(opt)) base.perle_options = [...(base.perle_options||[]), opt];
        return base;
    });
    const [photos, setPhotos] = useState([]); // {id, url, name}
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null); // request_number
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!done) localStorage.setItem("kgd_soumission_draft", JSON.stringify(data));
    }, [data, done]);

    const set = (k, v) => setData(d => ({ ...d, [k]: v }));

    const validateStep = () => {
        const e = {};
        if (step === 0) {
            if (!data.first_name.trim()) e.first_name = "Requis";
            if (!data.last_name.trim()) e.last_name = "Requis";
            if (!data.phone.trim() || data.phone.replace(/\D/g,'').length < 10) e.phone = "Téléphone valide requis";
            if (!/^\S+@\S+\.\S+$/.test(data.email)) e.email = "Courriel valide requis";
            if (!data.city.trim()) e.city = "Requis";
        }
        if (step === 1) {
            if (!data.property_type) e.property_type = "Requis";
        }
        if (step === 2) {
            if (!data.description.trim() || data.description.trim().length < 10) e.description = "Décrivez brièvement vos besoins (min. 10 caractères)";
        }
        if (step === 3) {
            if (!data.consent) e.consent = "Consentement requis";
            if (!data.privacy_accepted) e.privacy_accepted = "Acceptation requise";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const next = () => { if (validateStep()) setStep(s => Math.min(s + 1, STEPS.length - 1)); };
    const prev = () => setStep(s => Math.max(s - 1, 0));

    const handleUpload = async (files) => {
        for (const f of Array.from(files).slice(0, 8)) {
            const fd = new FormData();
            fd.append("file", f);
            try {
                const { data: res } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
                setPhotos(p => [...p, { id: res.id, url: `${process.env.REACT_APP_BACKEND_URL}${res.url}`, name: f.name }]);
                setData(d => ({ ...d, photo_ids: [...d.photo_ids, res.id] }));
            } catch (err) {
                toast.error(`Échec téléversement: ${formatApiErrorDetail(err.response?.data?.detail)}`);
            }
        }
    };

    const removePhoto = (id) => {
        setPhotos(p => p.filter(x => x.id !== id));
        setData(d => ({ ...d, photo_ids: d.photo_ids.filter(x => x !== id) }));
    };

    const submit = async () => {
        if (!validateStep()) return;
        setSubmitting(true);
        try {
            const { data: res } = await api.post("/submissions", data);
            setDone(res.request_number);
            localStorage.removeItem("kgd_soumission_draft");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="max-w-2xl mx-auto px-5 lg:px-8 py-20 text-center">
                <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg,#E5C158,#D4AF37)" }}>
                    <CheckCircle2 className="w-8 h-8 text-[#071A33]"/>
                </div>
                <h1 className="font-serif text-3xl sm:text-4xl text-[#071A33] mt-6">Merci!</h1>
                <p className="text-slate-700 mt-3">Votre demande de soumission a bien été reçue. L'équipe de Ménage KGD communiquera avec vous prochainement.</p>
                <div className="mt-6 inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#071A33] text-white">
                    <span className="eyebrow" style={{ color: "#F0E4B4" }}>Numéro</span>
                    <span className="font-mono tracking-wider" data-testid="soumission-request-number">{done}</span>
                </div>
                <div className="mt-8">
                    <Link to="/" className="btn-gold" data-testid="soumission-back-home">Retour à l'accueil</Link>
                </div>
            </div>
        );
    }

    const inputCls = "w-full rounded-lg border border-slate-300 px-4 py-3 text-[#071A33] focus:border-kgd-gold focus:ring-2 focus:ring-kgd-gold/25 outline-none bg-white";

    return (
        <div>
            <section className="py-12 lg:py-16" style={{ background: "linear-gradient(160deg,#050F22,#0A2555)" }} data-theme="dark">
                <div className="max-w-3xl mx-auto px-5 lg:px-8">
                    <div className="eyebrow">Évaluation gratuite</div>
                    <h1 className="font-serif text-3xl sm:text-4xl text-white mt-3">Planifier votre évaluation gratuite</h1>
                    <p className="text-white/80 mt-3 text-sm sm:text-base leading-relaxed">Parlez-nous brièvement de votre espace et de vos besoins. Notre équipe vous contactera afin de planifier une évaluation sur place et préparer une soumission adaptée.</p>
                    <p className="mt-3 text-xs" style={{ color: "#CBD5E1" }}>Évaluation gratuite • Sans engagement • Soumission personnalisée</p>
                </div>
            </section>

            <section className="max-w-3xl mx-auto px-5 lg:px-8 py-10">
                {/* Progress */}
                <div className="mb-8">
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                        <span data-testid="soumission-step-label">Étape {step + 1} sur {STEPS.length} — {STEPS[step]}</span>
                        <span>{Math.round(((step+1)/STEPS.length)*100)}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full transition-all duration-500" style={{ width: `${((step+1)/STEPS.length)*100}%`, background: "linear-gradient(90deg,#008CFF,#00B8FF 55%,#39D600)" }}/>
                    </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm">
                    {step === 0 && (
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Prénom *" err={errors.first_name}>
                                <input data-testid="f-first_name" className={inputCls} value={data.first_name} onChange={e => set("first_name", e.target.value)}/>
                            </Field>
                            <Field label="Nom *" err={errors.last_name}>
                                <input data-testid="f-last_name" className={inputCls} value={data.last_name} onChange={e => set("last_name", e.target.value)}/>
                            </Field>
                            <Field label="Téléphone *" err={errors.phone}>
                                <input data-testid="f-phone" className={inputCls} placeholder="819-555-0199" value={data.phone} onChange={e => set("phone", e.target.value)}/>
                            </Field>
                            <Field label="Courriel *" err={errors.email}>
                                <input data-testid="f-email" type="email" className={inputCls} value={data.email} onChange={e => set("email", e.target.value)}/>
                            </Field>
                            <Field label="Ville *" err={errors.city}>
                                <input data-testid="f-city" className={inputCls} placeholder="Trois-Rivières" value={data.city} onChange={e => set("city", e.target.value)}/>
                            </Field>
                            <Field label="Adresse (facultatif)">
                                <input data-testid="f-address" className={inputCls} value={data.address} onChange={e => set("address", e.target.value)}/>
                            </Field>
                            {/* Honeypot */}
                            <input type="text" tabIndex="-1" autoComplete="off" style={{position:"absolute",left:"-9999px",opacity:0}} value={data.honeypot} onChange={e => set("honeypot", e.target.value)}/>
                        </div>
                    )}

                    {step === 1 && (
                        <div className="grid gap-5">
                            <Field label="Type d'espace *" err={errors.property_type}>
                                <div className="grid grid-cols-2 gap-3">
                                    {[["residentiel","Résidentiel"],["commercial","Commercial"]].map(([v,l]) => (
                                        <button key={v} type="button" data-testid={`f-property-${v}`} onClick={() => set("property_type", v)}
                                            className={`px-4 py-3 rounded-lg border text-sm ${data.property_type===v ? "border-kgd-gold bg-kgd-gold/10 text-[#071A33]" : "border-slate-300 text-slate-600 hover:border-slate-400"}`}>{l}</button>
                                    ))}
                                </div>
                            </Field>
                            <Field label="Collection souhaitée">
                                <select data-testid="f-collection" className={inputCls} value={data.collection} onChange={e => set("collection", e.target.value)}>
                                    <option value="">— Je ne sais pas quelle collection choisir —</option>
                                    {COLLECTIONS.map(c => <option key={c.slug} value={c.slug}>{`Collection ${c.name} — ${c.subtitle}`}</option>)}
                                </select>
                            </Field>
                            <Field label="Type de propriété ou d'établissement">
                                <input data-testid="f-establishment" className={inputCls} placeholder={data.property_type === "commercial" ? "Bureau, clinique, commerce..." : "Maison, condo, appartement..."} value={data.establishment_type} onChange={e => set("establishment_type", e.target.value)}/>
                            </Field>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Nombre approximatif de pièces">
                                    <input data-testid="f-rooms" className={inputCls} value={data.rooms} onChange={e => set("rooms", e.target.value)}/>
                                </Field>
                                <Field label="Nombre de salles de bain">
                                    <input data-testid="f-bathrooms" className={inputCls} value={data.bathrooms} onChange={e => set("bathrooms", e.target.value)}/>
                                </Field>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="grid gap-5">
                            <Field label="Fréquence souhaitée">
                                <select data-testid="f-frequency" className={inputCls} value={data.frequency} onChange={e => set("frequency", e.target.value)}>
                                    <option value="">— Choisir —</option>
                                    <option>Hebdomadaire</option>
                                    <option>Aux 2 semaines</option>
                                    <option>Aux 3 semaines</option>
                                    <option>Mensuel</option>
                                    <option>Ponctuel / à la carte</option>
                                </select>
                            </Field>
                            <Field label="Options Perle à ajouter (facultatif)">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {PERLE_OPTIONS.map(o => {
                                        const active = data.perle_options.includes(o.key);
                                        return (
                                            <button
                                                key={o.key} type="button"
                                                data-testid={`f-perle-${o.key}`}
                                                onClick={() => set("perle_options", active ? data.perle_options.filter(x => x !== o.key) : [...data.perle_options, o.key])}
                                                className={`text-left rounded-lg border px-3 py-2 text-sm transition-all ${active ? "border-kgd-gold bg-kgd-gold/10 text-[#041126]" : "border-slate-300 text-slate-700 hover:border-slate-400"}`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span className="font-medium">{o.label}</span>
                                                    <span className="text-xs text-slate-500">dès {o.from}</span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </Field>
                            <Field label="Niveau d'investissement envisagé (facultatif)">
                                <div className="text-xs text-slate-500 mb-2">Cette information nous aide à mieux comprendre vos attentes — vous ne vous engagez à aucun montant.</div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {INVESTMENT_LEVELS.map(inv => (
                                        <button key={inv.key} type="button" data-testid={`f-inv-${inv.key}`}
                                            onClick={() => set("investment_level", inv.key)}
                                            className={`text-left rounded-lg border px-3 py-2 text-sm transition-all ${data.investment_level === inv.key ? "border-kgd-gold bg-kgd-gold/10 text-[#041126]" : "border-slate-300 text-slate-700 hover:border-slate-400"}`}>
                                            {inv.label}
                                        </button>
                                    ))}
                                </div>
                            </Field>
                            <Field label="Date ou période préférée">
                                <input data-testid="f-preferred_date" className={inputCls} placeholder="ex. Semaine du 15 mars, en avant-midi" value={data.preferred_date} onChange={e => set("preferred_date", e.target.value)}/>
                            </Field>
                            <Field label="Description des besoins *" err={errors.description}>
                                <textarea data-testid="f-description" rows="5" className={inputCls} placeholder="Décrivez brièvement l'espace, les pièces à traiter, les particularités..." value={data.description} onChange={e => set("description", e.target.value)}/>
                            </Field>
                            <Field label="Photos (facultatif)">
                                <label htmlFor="photo-input" className="flex items-center gap-2 border border-dashed border-slate-300 rounded-lg px-4 py-4 text-sm text-slate-600 cursor-pointer hover:border-kgd-gold">
                                    <Upload className="w-4 h-4"/> Téléverser des photos (jusqu'à 8, 10 Mo max chacune)
                                </label>
                                <input id="photo-input" data-testid="f-photos" type="file" multiple accept="image/*" className="hidden" onChange={e => handleUpload(e.target.files)}/>
                                {photos.length > 0 && (
                                    <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                                        {photos.map(p => (
                                            <div key={p.id} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200">
                                                <img src={p.url} alt={p.name} className="w-full h-full object-cover"/>
                                                <button onClick={() => removePhoto(p.id)} data-testid={`f-remove-${p.id}`} className="absolute top-1 right-1 bg-black/60 rounded-full p-1">
                                                    <X className="w-3 h-3 text-white"/>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Field>
                            <Field label="Méthode de contact préférée">
                                <div className="flex gap-3">
                                    {[["phone","Téléphone"],["email","Courriel"],["either","Sans préférence"]].map(([v,l]) => (
                                        <button key={v} type="button" data-testid={`f-contact-${v}`} onClick={() => set("contact_method", v)}
                                            className={`px-4 py-2 rounded-full border text-sm ${data.contact_method===v ? "border-kgd-gold bg-kgd-gold/10 text-[#071A33]" : "border-slate-300 text-slate-600"}`}>{l}</button>
                                    ))}
                                </div>
                            </Field>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-5">
                            <h3 className="font-serif text-xl text-[#071A33]">Récapitulatif</h3>
                            <div className="grid sm:grid-cols-2 gap-3 text-sm text-slate-700">
                                <div><b>Nom:</b> {data.first_name} {data.last_name}</div>
                                <div><b>Téléphone:</b> {data.phone}</div>
                                <div><b>Courriel:</b> {data.email}</div>
                                <div><b>Ville:</b> {data.city}</div>
                                <div><b>Type:</b> {data.property_type}</div>
                                <div><b>Collection:</b> {data.collection || "à déterminer"}</div>
                                <div><b>Fréquence:</b> {data.frequency || "—"}</div>
                                <div><b>Période:</b> {data.preferred_date || "—"}</div>
                            </div>
                            <div className="text-sm text-slate-700 border rounded-lg p-3 bg-[#F7F8FA]"><b>Besoins:</b> {data.description}</div>
                            <label className="flex items-start gap-3 text-sm text-slate-700">
                                <input data-testid="f-consent" type="checkbox" checked={data.consent} onChange={e => set("consent", e.target.checked)} className="mt-1"/>
                                <span>J'accepte d'être contacté(e) par Ménage KGD au sujet de ma demande.</span>
                            </label>
                            {errors.consent && <p className="text-xs text-red-600 -mt-2">{errors.consent}</p>}
                            <label className="flex items-start gap-3 text-sm text-slate-700">
                                <input data-testid="f-privacy" type="checkbox" checked={data.privacy_accepted} onChange={e => set("privacy_accepted", e.target.checked)} className="mt-1"/>
                                <span>J'ai lu et j'accepte la <Link to="/confidentialite" className="text-kgd-blue underline">politique de confidentialité</Link>.</span>
                            </label>
                            {errors.privacy_accepted && <p className="text-xs text-red-600 -mt-2">{errors.privacy_accepted}</p>}
                        </div>
                    )}

                    <div className="mt-8 flex items-center justify-between">
                        <button onClick={prev} disabled={step===0} data-testid="soumission-prev" className="inline-flex items-center gap-1 text-sm text-slate-600 disabled:opacity-30">
                            <ChevronLeft className="w-4 h-4"/> Précédent
                        </button>
                        {step < STEPS.length - 1 ? (
                            <button onClick={next} data-testid="soumission-next" className="btn-kgd">Suivant <ChevronRight className="w-4 h-4"/></button>
                        ) : (
                            <button onClick={submit} disabled={submitting} data-testid="soumission-submit" className="btn-kgd">
                                <Sparkles className="w-4 h-4"/> {submitting ? "Envoi..." : "Planifier mon évaluation"}
                            </button>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}

function Field({ label, err, children }) {
    return (
        <label className="block">
            <div className="text-sm text-slate-700 mb-1.5">{label}</div>
            {children}
            {err && <div className="text-xs text-red-600 mt-1">{err}</div>}
        </label>
    );
}
